import { c as _c } from "react/compiler-runtime";
import { getCountryData, countriesData } from '@/data/countriesData';
import CountryImage from '@/components/shared/CountryImage';
import SeoHead from '@/components/seo/SeoHead';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { formatProse } from '@/lib/formatCopy';
import { breadcrumbJsonLd, seoForCountry } from '@/lib/seo';
import { Calendar, Users, Globe2, MapPin } from 'lucide-react';
const PAGE_STYLE = `
    .country-hero-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top center;
    }
`;
const REGION_COLORS = {
  Caribbean: {
    badge: '#D4881B',
    dot: '#D4881B'
  },
  'South America': {
    badge: '#2E7D32',
    dot: '#4CAF50'
  },
  'Indian Ocean': {
    badge: '#1565C0',
    dot: '#42A5F5'
  },
  'Pacific Ocean': {
    badge: '#6A1B9A',
    dot: '#AB47BC'
  },
  'East Africa': {
    badge: '#1B5E20',
    dot: '#66BB6A'
  },
  'Southeast Asia': {
    badge: '#00695C',
    dot: '#26A69A'
  },
  'Southern Africa': {
    badge: '#4527A0',
    dot: '#7E57C2'
  },
  default: {
    badge: '#DF6D2A',
    dot: '#DF6D2A'
  }
};
function getRegionColor(region) {
  return REGION_COLORS[region] || REGION_COLORS.default;
}
function SectionLabel(t0) {
  const $ = _c(8);
  const {
    children,
    color
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = {
      color
    };
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <span className="font-body text-[11px] sm:text-[12px] tracking-[0.22em] uppercase font-semibold" style={t1}>{children}</span>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex-1 h-px bg-gray-200" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div className="flex items-center gap-3 mb-5">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function figureImageSrc(path) {
  if (!path) return null;
  return encodeURI(path);
}
function FigurePortrait(t0) {
  const $ = _c(19);
  const {
    fig,
    color
  } = t0;
  let t1;
  if ($[0] !== fig.image) {
    t1 = figureImageSrc(fig.image);
    $[0] = fig.image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const src = t1;
  let t2;
  if ($[2] !== fig.initials || $[3] !== fig.name) {
    t2 = fig.initials || fig.name.split(" ").map(_temp).join("").slice(0, 2);
    $[2] = fig.initials;
    $[3] = fig.name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initials = t2;
  if (src) {
    const t3 = `inset 0 0 0 1px ${color}33`;
    let t4;
    if ($[5] !== t3) {
      t4 = {
        boxShadow: t3
      };
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const t5 = fig.imageAlt || `Portrait of ${fig.name}`;
    let t6;
    if ($[7] !== src || $[8] !== t5) {
      t6 = <img src={src} alt={t5} className="w-full h-full object-cover object-top" />;
      $[7] = src;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0F1C2E]/50 to-transparent pointer-events-none" aria-hidden={true} />;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t4 || $[12] !== t6) {
      t8 = <div className="relative w-full sm:w-[200px] lg:w-[220px] shrink-0 aspect-[3/4] overflow-hidden bg-[#E8E4DC]" style={t4}>{t6}{t7}</div>;
      $[11] = t4;
      $[12] = t6;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  let t3;
  if ($[14] !== color) {
    t3 = {
      color,
      borderColor: color
    };
    $[14] = color;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== initials || $[17] !== t3) {
    t4 = <div className="w-full sm:w-[200px] lg:w-[220px] shrink-0 aspect-[3/4] flex items-center justify-center font-display text-[48px] font-semibold bg-[#F4EFE6] border border-gray-200" style={t3}>{initials}</div>;
    $[16] = initials;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}
function _temp(n) {
  return n[0];
}
function FigureCard(t0) {
  const $ = _c(25);
  const {
    fig,
    color,
    index
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 20
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      once: true
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  const t4 = index * 0.08;
  let t5;
  if ($[3] !== t4) {
    t5 = {
      duration: 0.5,
      delay: t4
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== color || $[6] !== fig) {
    t6 = <FigurePortrait fig={fig} color={color} />;
    $[5] = color;
    $[6] = fig;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== color) {
    t7 = {
      color
    };
    $[8] = color;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== fig.role || $[11] !== t7) {
    t8 = <p className="font-body text-[11px] sm:text-[12px] tracking-[0.2em] uppercase font-semibold mb-2" style={t7}>{fig.role}</p>;
    $[10] = fig.role;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== fig.name) {
    t9 = <h3 className="font-display text-[26px] sm:text-[30px] lg:text-[34px] font-semibold text-[#1A2C42] leading-[1.1] mb-3 sm:mb-4">{fig.name}</h3>;
    $[13] = fig.name;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== fig.desc) {
    t10 = <p className="text-[#4C596B] text-[15px] sm:text-[16px] leading-[1.7] max-w-[520px]">{fig.desc}</p>;
    $[15] = fig.desc;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8 || $[19] !== t9) {
    t11 = <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 min-w-0 flex-1">{t8}{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t5 || $[23] !== t6) {
    t12 = <motion.article initial={t1} whileInView={t2} viewport={t3} transition={t5} className="flex flex-col sm:flex-row overflow-hidden border border-gray-200 bg-white shadow-[0_4px_24px_rgba(15,28,46,0.06)]">{t6}{t11}</motion.article>;
    $[21] = t11;
    $[22] = t5;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function TimelineDot(t0) {
  const $ = _c(3);
  const {
    color
  } = t0;
  const t1 = `0 0 8px ${color}88`;
  let t2;
  if ($[0] !== color || $[1] !== t1) {
    t2 = <span className="w-3.5 h-3.5 rounded-full shrink-0 mt-1.5" style={{
      background: color,
      boxShadow: t1
    }} />;
    $[0] = color;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function DetailBlock(t0) {
  const $ = _c(15);
  const {
    title,
    items,
    color,
    dotColor
  } = t0;
  if (!items?.length) {
    return null;
  }
  let t1;
  if ($[0] !== color) {
    t1 = {
      color
    };
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== title) {
    t2 = <h3 className="font-body text-[11px] sm:text-[12px] tracking-[0.2em] uppercase font-semibold mb-3" style={t1}>{title}</h3>;
    $[2] = t1;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== dotColor || $[6] !== items) {
    let t4;
    if ($[8] !== dotColor) {
      t4 = (item, i) => <li key={i} className="font-body text-[#374151] text-[15px] sm:text-[16px] leading-[1.65] flex gap-2.5"><span className="shrink-0 font-bold" style={{
          color: dotColor
        }}>•</span>{item}</li>;
      $[8] = dotColor;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    t3 = items.map(t4);
    $[5] = dotColor;
    $[6] = items;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <ul className="flex flex-col gap-2.5">{t3}</ul>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div>{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function CountryIntro(t0) {
  const $ = _c(43);
  const {
    country,
    rc,
    hasDetails,
    titleMode: t1
  } = t0;
  const titleMode = t1 === undefined ? "desktop" : t1;
  const showTitle = titleMode === "mobile" || titleMode === "desktop";
  const titleDesktop = titleMode === "desktop";
  const titleMobile = titleMode === "mobile";
  let t2;
  if ($[0] !== country.name || $[1] !== country.region || $[2] !== rc.badge || $[3] !== showTitle || $[4] !== titleDesktop || $[5] !== titleMobile) {
    t2 = showTitle && <div className={`mb-5 ${titleDesktop ? "hidden lg:block" : ""} ${titleMobile ? "lg:hidden" : ""}`}><h1 className={`font-display font-semibold text-[#1A2C42] leading-[1.02] ${titleMobile ? "text-[36px] sm:text-[40px]" : "text-[48px] xl:text-[56px]"}`}>{country.name}</h1>{country.region && <p className="text-[13px] tracking-[0.2em] uppercase mt-3 italic font-medium" style={{
        color: rc.badge
      }}>({country.region})</p>}</div>;
    $[0] = country.name;
    $[1] = country.region;
    $[2] = rc.badge;
    $[3] = showTitle;
    $[4] = titleDesktop;
    $[5] = titleMobile;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== rc.badge) {
    t3 = <div className="w-14 h-px" style={{
      background: rc.badge
    }} />;
    $[7] = rc.badge;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex-1 h-px bg-gray-200" />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3) {
    t5 = <div className="flex items-center gap-3 mb-6">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== country.subtitle) {
    t6 = country.subtitle && <p className="font-display italic text-[#8B6E30] text-[17px] sm:text-[18px] leading-[1.55] mb-5">{formatProse(country.subtitle)}</p>;
    $[12] = country.subtitle;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== country.description) {
    t7 = country.description && <p className="text-[#374151] text-[15px] sm:text-[16px] leading-[1.75] mb-6">{formatProse(country.description)}</p>;
    $[14] = country.description;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== country.mainLocation || $[17] !== rc.badge) {
    t8 = country.mainLocation && <div className="flex items-start gap-2.5 mb-8 text-[15px] text-[#374151]"><MapPin size={18} className="shrink-0 mt-0.5" style={{
        color: rc.badge
      }} /><p><span className="font-semibold text-[#1A2C42]">{country.mainLocation}</span><span className="text-[#6B7280] ml-1.5">· main location</span></p></div>;
    $[16] = country.mainLocation;
    $[17] = rc.badge;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== country.timeline || $[20] !== rc.badge || $[21] !== rc.dot) {
    t9 = country.timeline?.length > 0 && <div className="mb-8"><SectionLabel color={rc.badge}>Major Historical Events</SectionLabel><div className="flex flex-col gap-5">{country.timeline.map((item, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: idx * 0.06
        }} className="flex gap-4 items-start"><TimelineDot color={rc.dot} /><div className="flex flex-col sm:flex-row sm:gap-4 sm:items-start min-w-0"><span className="font-semibold text-[15px] sm:text-[16px] whitespace-nowrap shrink-0" style={{
              color: rc.badge,
              minWidth: "72px"
            }}>{item.year}</span><p className="text-[#374151] text-[15px] sm:text-[16px] leading-[1.65]">{item.desc}</p></div></motion.div>)}</div></div>;
    $[19] = country.timeline;
    $[20] = rc.badge;
    $[21] = rc.dot;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== country.extraNote || $[24] !== rc.badge) {
    t10 = country.extraNote && <p className="italic text-[#8B6E30] text-[15px] leading-relaxed mb-6 border-l-[3px] pl-4" style={{
      borderColor: rc.badge
    }}>{country.extraNote}</p>;
    $[23] = country.extraNote;
    $[24] = rc.badge;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== country.challenges || $[27] !== country.contributions || $[28] !== country.culturalLegacy || $[29] !== country.occupations || $[30] !== hasDetails || $[31] !== rc.badge || $[32] !== rc.dot) {
    t11 = hasDetails && <div className="mt-2 pt-8 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8"><DetailBlock title="Important Contributions" items={country.contributions} color={rc.badge} dotColor={rc.dot} /><DetailBlock title="Main Occupations" items={country.occupations} color={rc.badge} dotColor={rc.dot} /><DetailBlock title="Cultural Legacy Today" items={country.culturalLegacy} color={rc.badge} dotColor={rc.dot} /><DetailBlock title="Challenges Faced" items={country.challenges} color={rc.badge} dotColor={rc.dot} /></div>;
    $[26] = country.challenges;
    $[27] = country.contributions;
    $[28] = country.culturalLegacy;
    $[29] = country.occupations;
    $[30] = hasDetails;
    $[31] = rc.badge;
    $[32] = rc.dot;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t2 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7 || $[40] !== t8 || $[41] !== t9) {
    t12 = <>{t2}{t5}{t6}{t7}{t8}{t9}{t10}{t11}</>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t2;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}
export default function CountryPage(t0) {
  const $ = _c(93);
  const {
    country
  } = t0;
  if (!country) {
    return null;
  }
  let t1;
  if ($[0] !== country.region) {
    t1 = getRegionColor(country.region);
    $[0] = country.region;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rc = t1;
  const hasDetails = country.contributions?.length > 0 || country.occupations?.length > 0 || country.culturalLegacy?.length > 0 || country.challenges?.length > 0;
  let t2;
  if ($[2] !== country) {
    t2 = seoForCountry(country);
    $[2] = country;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== country.name || $[5] !== country.slug) {
    t3 = breadcrumbJsonLd([{
      name: "Home",
      path: "/"
    }, {
      name: "Countries",
      path: "/countries"
    }, {
      name: country.name,
      path: `/countries/${country.slug}`
    }]);
    $[4] = country.name;
    $[5] = country.slug;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <SeoHead {...t2} jsonLd={t3} />;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <style>{PAGE_STYLE}</style>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = `${country.name}, Girmitiya destination heritage image`;
  let t7;
  if ($[11] !== country.heroImage || $[12] !== t6) {
    t7 = <div className="relative w-full aspect-square max-h-[min(100vw,400px)] overflow-hidden bg-[#FDFBF7] border-b border-[#0F1C2E]/10"><CountryImage src={country.heroImage} alt={t6} variant="hero" /></div>;
    $[11] = country.heroImage;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== country || $[15] !== hasDetails || $[16] !== rc) {
    t8 = <div className="px-5 sm:px-6 pt-6 pb-10"><CountryIntro country={country} rc={rc} hasDetails={hasDetails} titleMode="mobile" /></div>;
    $[14] = country;
    $[15] = hasDetails;
    $[16] = rc;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="lg:hidden w-full">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = `${country.name}, Girmitiya destination heritage image`;
  let t11;
  if ($[21] !== country.heroImage || $[22] !== t10) {
    t11 = <div className="relative shrink-0 overflow-hidden bg-[#FDFBF7] border-r border-[#0F1C2E]/10 lg:sticky lg:top-[106px] lg:self-start lg:w-[min(100vw,calc(100vh-106px))] lg:h-[min(100vw,calc(100vh-106px))]"><CountryImage src={country.heroImage} alt={t10} variant="hero" /></div>;
    $[21] = country.heroImage;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== country || $[25] !== hasDetails || $[26] !== rc) {
    t12 = <div className="flex flex-col min-w-0 px-10 xl:px-12 py-10 min-h-[calc(100vh-106px)]"><CountryIntro country={country} rc={rc} hasDetails={hasDetails} titleMode="desktop" /></div>;
    $[24] = country;
    $[25] = hasDetails;
    $[26] = rc;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <div className="hidden lg:grid lg:grid-cols-[auto_1fr] w-full">{t11}{t12}</div>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t9) {
    t14 = <section className="w-full">{t9}{t13}</section>;
    $[31] = t13;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== rc.badge) {
    t15 = <Calendar size={40} className="shrink-0 opacity-80" strokeWidth={1.5} color={rc.badge} />;
    $[34] = rc.badge;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== rc.badge) {
    t16 = {
      color: rc.badge
    };
    $[36] = rc.badge;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = country.periodStart && country.periodEnd ? `${country.periodStart} to ${country.periodEnd}` : country.periodStart || country.periodEnd || "Not recorded";
  let t18;
  if ($[38] !== t16 || $[39] !== t17) {
    t18 = <p className="font-display font-semibold text-[26px] sm:text-[30px] leading-none" style={t16}>{t17}</p>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p className="font-body text-[12px] tracking-[0.18em] uppercase text-[#6B7280] mt-2 font-medium">Period</p>;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t18) {
    t20 = <div>{t18}{t19}</div>;
    $[42] = t18;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t15 || $[45] !== t20) {
    t21 = <div className="flex items-center gap-5 px-8 sm:px-10 py-8 sm:py-9">{t15}{t20}</div>;
    $[44] = t15;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== rc.badge) {
    t22 = <Users size={40} className="shrink-0 opacity-80" strokeWidth={1.5} color={rc.badge} />;
    $[47] = rc.badge;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== rc.badge) {
    t23 = {
      color: rc.badge
    };
    $[49] = rc.badge;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = country.totalTransported || "Not recorded";
  let t25;
  if ($[51] !== t23 || $[52] !== t24) {
    t25 = <p className="font-display font-semibold text-[26px] sm:text-[30px] leading-none" style={t23}>{t24}</p>;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <p className="font-body text-[12px] tracking-[0.18em] uppercase text-[#6B7280] mt-2 font-medium">Total Arrivals</p>;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t25) {
    t27 = <div>{t25}{t26}</div>;
    $[55] = t25;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t22 || $[58] !== t27) {
    t28 = <div className="flex items-center gap-5 px-8 sm:px-10 py-8 sm:py-9">{t22}{t27}</div>;
    $[57] = t22;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== rc.badge) {
    t29 = <Globe2 size={40} className="shrink-0 opacity-80" strokeWidth={1.5} color={rc.badge} />;
    $[60] = rc.badge;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== rc.badge) {
    t30 = {
      color: rc.badge
    };
    $[62] = rc.badge;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const t31 = country.region || "Global";
  let t32;
  if ($[64] !== t30 || $[65] !== t31) {
    t32 = <p className="font-display font-semibold text-[26px] sm:text-[30px] leading-none" style={t30}>{t31}</p>;
    $[64] = t30;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <p className="font-body text-[12px] tracking-[0.18em] uppercase text-[#6B7280] mt-2 font-medium">Region</p>;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== t32) {
    t34 = <div>{t32}{t33}</div>;
    $[68] = t32;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== t29 || $[71] !== t34) {
    t35 = <div className="flex items-center gap-5 px-8 sm:px-10 py-8 sm:py-9">{t29}{t34}</div>;
    $[70] = t29;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t21 || $[74] !== t28 || $[75] !== t35) {
    t36 = <section className="border-t border-gray-200 bg-white"><div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">{t21}{t28}{t35}</div></section>;
    $[73] = t21;
    $[74] = t28;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== country.figures || $[78] !== rc.badge) {
    t37 = country.figures?.length > 0 && <section className="border-t border-gray-200 bg-[#F9F7F2]"><div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 py-12 sm:py-16 lg:py-20"><SectionLabel color={rc.badge}>Key Girmitiya Figures</SectionLabel><div className={`grid gap-8 sm:gap-10 ${country.figures.length === 1 ? "grid-cols-1 max-w-[1100px]" : "grid-cols-1 xl:grid-cols-2"}`}>{country.figures.map((fig, idx) => <FigureCard key={fig.name} fig={fig} color={rc.badge} index={idx} />)}</div></div></section>;
    $[77] = country.figures;
    $[78] = rc.badge;
    $[79] = t37;
  } else {
    t37 = $[79];
  }
  let t38;
  if ($[80] !== country.reports || $[81] !== rc.badge) {
    t38 = country.reports?.length > 0 && <section className="border-t border-gray-200 bg-white"><div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 py-12 sm:py-16"><SectionLabel color={rc.badge}>{"Official Documents & Reports"}</SectionLabel><div className="flex flex-col divide-y divide-gray-100">{country.reports.map((report, idx_0) => <div key={idx_0} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-5 gap-2"><div><h3 className="font-display text-[20px] font-semibold text-[#1A2C42]">{report.title}</h3><p className="text-[#6B7280] text-[14px] mt-1">Authored by {report.author}</p></div><span className="font-display font-semibold text-[22px]" style={{
              color: rc.badge
            }}>{report.year}</span></div>)}</div></div></section>;
    $[80] = country.reports;
    $[81] = rc.badge;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span className="font-display text-white text-[20px] font-semibold uppercase tracking-wide">Girmitiya Council</span>;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  let t40;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <footer className="bg-[#1A2C42]"><div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-9 flex flex-col sm:flex-row items-center justify-between gap-6">{t39}<div className="flex flex-col items-center gap-1.5 text-center"><span className="text-white text-[12px] tracking-[0.18em] uppercase font-semibold">Preserving the Legacy</span><span className="text-white/50 text-[11px] tracking-[0.12em] uppercase">Preserving the Legacy of Indian Indentured Labourers, 1834 to 1917</span></div><Link href="/countries" className="text-[12px] tracking-[0.15em] uppercase text-[#D4881B] hover:text-white transition-colors font-semibold">← All Countries</Link></div></footer>;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== t14 || $[86] !== t36 || $[87] !== t37 || $[88] !== t38) {
    t41 = <div className="min-h-screen bg-[#FDFBF7] text-[#1A2C42] font-body">{t14}{t36}{t37}{t38}{t40}</div>;
    $[85] = t14;
    $[86] = t36;
    $[87] = t37;
    $[88] = t38;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  let t42;
  if ($[90] !== t4 || $[91] !== t41) {
    t42 = <>{t4}{t5}{t41}</>;
    $[90] = t4;
    $[91] = t41;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  return t42;
}
export async function getStaticPaths() {
  const paths = countriesData.map(country => ({
    params: {
      slug: country.slug
    }
  }));
  return {
    paths,
    fallback: 'blocking'
  };
}
export async function getStaticProps({
  params
}) {
  const country = getCountryData(params.slug);
  if (!country) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      country
    },
    revalidate: 60
  };
}